import { useRef, useState, DragEvent } from 'react';
import { useFileUpload } from '../../hooks/useFileUpload';
import styles from './FileUploadZone.module.css';

interface FileUploadZoneProps {
  projectId: string;
  allowedExtensions: string[];
  onUploadComplete: () => void;
  folderId?: string | null;
  sectionHint?: string;
}

type FileStatus = 'queued' | 'uploading' | 'done' | 'error';

interface FileEntry {
  id: string;
  file: File;
  status: FileStatus;
  progress: number;
  speed?: number;
  eta?: number;
  error?: string;
}

// A .zip is a mesh bundle (OBJ/glTF + textures) in the 3D section and a
// zipped ReCap bundle everywhere else; .lgsx is a Leica tour bundle.
export const extensionLabel = (ext: string, fileType?: string): string => {
  const e = (ext || '').toLowerCase();
  if (e === 'zip') return fileType === 'three_d' ? 'zip (obj bundle)' : 'zip (rcp)';
  if (e === 'lgsx') return 'lgsx (Leica)';
  return ext;
};

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatSpeed(bytesPerSec: number): string {
  if (bytesPerSec >= 1024 * 1024 * 1024) return `${(bytesPerSec / (1024 * 1024 * 1024)).toFixed(1)} GB/s`;
  if (bytesPerSec >= 1024 * 1024) return `${(bytesPerSec / (1024 * 1024)).toFixed(1)} MB/s`;
  if (bytesPerSec >= 1024) return `${(bytesPerSec / 1024).toFixed(0)} KB/s`;
  return `${bytesPerSec.toFixed(0)} B/s`;
}

function formatEta(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s left`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s left`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m left`;
}

const FileUploadZone = ({ projectId, allowedExtensions, onUploadComplete, folderId, sectionHint }: FileUploadZoneProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileEntries, setFileEntries] = useState<FileEntry[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const { uploadFile } = useFileUpload(projectId);

  const updateEntry = (id: string, patch: Partial<FileEntry>) =>
    setFileEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));

  const handleFiles = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;

    const valid: FileEntry[] = [];
    for (const file of Array.from(files)) {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (!allowedExtensions.includes(ext)) {
        alert(`File type .${ext} is not supported. Allowed: ${allowedExtensions.join(', ')}`);
        continue;
      }
      valid.push({ id: `${file.name}-${Date.now()}-${Math.random()}`, file, status: 'queued', progress: 0 });
    }

    if (valid.length === 0) return;

    setFileEntries((prev) => [...prev, ...valid]);
    setIsProcessing(true);

    let anySucceeded = false;
    for (const entry of valid) {
      updateEntry(entry.id, { status: 'uploading' });
      try {
        await uploadFile(entry.file, (pct, bytesUploaded, speed) => {
          const remaining = entry.file.size - bytesUploaded;
          const eta = speed > 0 ? remaining / speed : undefined;
          updateEntry(entry.id, { progress: pct, speed, eta });
        }, folderId, sectionHint);
        updateEntry(entry.id, { status: 'done', progress: 100, speed: undefined, eta: undefined });
        anySucceeded = true;
      } catch (err) {
        updateEntry(entry.id, { status: 'error', error: err instanceof Error ? err.message : 'Upload failed' });
      }
    }

    setIsProcessing(false);
    if (anySucceeded) onUploadComplete();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const dropZoneClass = [
    styles.dropZone,
    isDragOver ? styles.dropZoneActive : '',
    isProcessing ? styles.dropZoneUploading : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div>
      <div
        className={dropZoneClass}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !isProcessing && fileInputRef.current?.click()}
      >
        <span className="material-symbols-outlined text-4xl text-primary mb-3 block">cloud_upload</span>
        <p className="text-sm font-medium text-on-surface mb-1">Drop files here or click to browse</p>
        <p className="text-xs text-on-surface-variant">
          Supported: {allowedExtensions.map((e) => `.${extensionLabel(e, sectionHint)}`).join(', ')}
        </p>
      </div>

      {fileEntries.length > 0 && (
        <div className={styles.fileList}>
          {fileEntries.map((entry) => (
            <div key={entry.id} className={styles.fileEntry}>
              <span className={`material-symbols-outlined ${styles.fileIcon}`}>insert_drive_file</span>
              <div className={styles.fileInfo}>
                <span className={styles.fileName}>{entry.file.name}</span>
                <span className={styles.fileSize}>{formatSize(entry.file.size)}</span>
                {entry.status === 'error' && entry.error && (
                  <span className={styles.fileErrorMessage}>
                    <span className="material-symbols-outlined">error</span>
                    {entry.error}
                  </span>
                )}
              </div>
              <div className={styles.fileStatus}>
                {entry.status === 'queued' && (
                  <span className={styles.statusQueued}>Queued</span>
                )}
                {entry.status === 'uploading' && (
                  <div className={styles.uploadProgress}>
                    <div className={styles.progressBar}>
                      <div className={styles.progressFill} style={{ width: `${entry.progress}%` }} />
                    </div>
                    <div className={styles.progressMeta}>
                      <span className={styles.progressText}>{entry.progress}%</span>
                      {entry.speed != null && entry.speed > 0 && (
                        <span className={styles.progressSpeed}>{formatSpeed(entry.speed)}</span>
                      )}
                      {entry.eta != null && entry.eta > 0 && (
                        <span className={styles.progressEta}>{formatEta(entry.eta)}</span>
                      )}
                    </div>
                  </div>
                )}
                {entry.status === 'done' && (
                  <span className={styles.statusDone}>
                    <span className="material-symbols-outlined">check_circle</span>
                    Done
                  </span>
                )}
                {entry.status === 'error' && (
                  <span className={styles.statusErrorIcon}>
                    <span className="material-symbols-outlined">error</span>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        accept={allowedExtensions.map((e) => `.${e}`).join(',')}
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          // Without the reset, picking the same file again fires no change event.
          e.target.value = '';
          handleFiles(picked);
        }}
      />
    </div>
  );
};

export default FileUploadZone;
